import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, HeartHandshake } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { DPS_MEDIA } from "../lib/content";
import { API } from "../lib/eventUtils";
import { trackEvent } from "../lib/analytics";

const TIERS = [
  { key: "donate_10", amount: "$10", label: "Fuel a Session", body: "Covers chalk, tape and the small stuff that keeps every session running." },
  { key: "donate_25", amount: "$25", label: "Back an Athlete", body: "Helps subsidize a spot so cost never keeps an athlete off the floor." },
  { key: "donate_50", amount: "$50", label: "Power a Sim", body: "Goes toward equipment and setup for full race simulations." },
  { key: "donate_100", amount: "$100", label: "Build the Program", body: "Directly funds new equipment, venues and community race-day events." },
];

export default function Support() {
  const [busyKey, setBusyKey] = useState(null);

  const donate = async (key) => {
    setBusyKey(key);
    try {
      const { data } = await axios.post(`${API}/api/donations/checkout`, {
        lookup_key: key,
        origin_url: window.location.origin,
      });
      trackEvent("donation_checkout_started", { tier: key });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not start checkout — please try again.");
      setBusyKey(null);
    }
  };

  return (
    <div data-testid="support-page" className="pt-16">
      <Seo
        title="Support the Program — HyroxNinja"
        description="Back the Dallas HYROX community — your support funds sessions, simulations, equipment and race-day events with Hyrox Ninja and Dallas Performance Systems."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.events} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
          <p className="eyebrow mb-4 flex items-center gap-2"><HeartHandshake size={14} /> Keep Dallas Racing</p>
          <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
            <MaskedLine delay={0.1}>Support</MaskedLine>
            <MaskedLine delay={0.25} className="text-volt">The Program</MaskedLine>
          </h1>
          <p className="text-zinc-400 max-w-2xl leading-relaxed">
            HyroxNinja is built by the community, for the community. Every dollar goes back into the floor —
            sessions, simulations, equipment and race-day events with{" "}
            <span className="text-white font-semibold">Dallas Performance Systems</span>.
          </p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {TIERS.map((t, i) => (
            <Reveal key={t.key} delay={i * 0.05} className="h-full">
              <article className="bg-[#0c0c0c] p-7 h-full flex flex-col" data-testid={`donation-tier-${t.key}`}>
                <p className="font-head font-black uppercase text-4xl text-volt mb-2">{t.amount}</p>
                <h2 className="font-head font-bold uppercase text-xl mb-2">{t.label}</h2>
                <p className="text-zinc-400 text-sm leading-relaxed mb-6">{t.body}</p>
                <button
                  onClick={() => donate(t.key)}
                  disabled={busyKey !== null}
                  className="btn-volt !py-2.5 !px-5 !text-sm mt-auto w-fit disabled:opacity-50"
                  data-testid={`donate-button-${t.key}`}
                >
                  {busyKey === t.key ? "Heading to checkout…" : `Give ${t.amount}`} {busyKey !== t.key && <ArrowRight size={15} />}
                </button>
              </article>
            </Reveal>
          ))}
        </div>
        <p className="font-mono2 text-[10px] uppercase tracking-[0.2em] text-zinc-600 mt-6">
          Secure checkout by Stripe · one-time payment · you'll get an on-screen confirmation
        </p>
      </section>
    </div>
  );
}
